import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  POPULATION_AGE_GROUPS,
  POPULATION_COMPARISONS,
  POPULATION_KINDS,
  POPULATION_PERIODS,
  POPULATION_RISK_LEVELS,
  type PopulationPeriod,
} from '@behuman/core-contract'
import type { PopulationQuery } from './hooks'
import { customRange } from './utils'

/** The filters (everything but period and comparison), in the URL under their API names. */
export const FILTER_KEYS = ['providerId', 'state', 'ageGroup', 'riskLevel', 'kind'] as const
export type FilterKey = (typeof FILTER_KEYS)[number]
export type ParamKey = 'comparison' | FilterKey

const DEFAULT_PERIOD = 'last-30-days'

const oneOf = <T extends string>(values: readonly T[], value: string | null): T | undefined =>
  values.find((v) => v === value)

/**
 * The dashboard's period, comparison and filters, kept in the URL so a reload or a shared link
 * restores them. Unknown values are ignored rather than sent to the API; so is a custom period
 * whose `start`/`end` the API would refuse, which falls back to the default period.
 */
export function usePopulationParams() {
  const [params, setParams] = useSearchParams()

  const query = useMemo<PopulationQuery>(() => {
    const period = oneOf(POPULATION_PERIODS, params.get('period')) ?? DEFAULT_PERIOD
    const range =
      period === 'custom' ? customRange(params.get('start'), params.get('end')) : undefined
    return {
      ...(period !== 'custom'
        ? { period }
        : range
          ? { period, ...range }
          : { period: DEFAULT_PERIOD }),
      comparison: oneOf(POPULATION_COMPARISONS, params.get('comparison')) ?? 'previous-period',
      providerId: params.get('providerId') || undefined,
      state: params.get('state') || undefined,
      ageGroup: oneOf(POPULATION_AGE_GROUPS, params.get('ageGroup')),
      riskLevel: oneOf(POPULATION_RISK_LEVELS, params.get('riskLevel')),
      kind: oneOf(POPULATION_KINDS, params.get('kind')),
    }
  }, [params])

  /** Sets the period; `range` is a custom period's dates, dropped for every other period. */
  const setPeriod = useCallback(
    (period: PopulationPeriod, range?: { start: string; end: string }) =>
      setParams((prev) => {
        const next = new URLSearchParams(prev)
        next.set('period', period)
        if (period === 'custom' && range) {
          next.set('start', range.start)
          next.set('end', range.end)
        } else {
          next.delete('start')
          next.delete('end')
        }
        return next
      }),
    [setParams],
  )

  const setParam = useCallback(
    (key: ParamKey, value: string | null) =>
      setParams((prev) => {
        const next = new URLSearchParams(prev)
        if (value) next.set(key, value)
        else next.delete(key)
        return next
      }),
    [setParams],
  )

  const clearFilters = useCallback(
    () =>
      setParams((prev) => {
        const next = new URLSearchParams(prev)
        for (const key of FILTER_KEYS) next.delete(key)
        return next
      }),
    [setParams],
  )

  const activeFilterCount = FILTER_KEYS.filter((key) => query[key] !== undefined).length

  return { query, setPeriod, setParam, clearFilters, activeFilterCount }
}
